import { useState, useEffect, useCallback } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { Input } from '@/components/ui/input';
import { MapPin, X } from 'lucide-react';

/**
 * PlaceAutocomplete - Google Places autocomplete for address search
 * Must be used inside an APIProvider context
 * 
 * @param {function} onPlaceSelect - Callback with { lat, lng, address }
 * @param {string} value - Current input value
 * @param {function} onChange - Callback when input changes
 */
export default function PlaceAutocomplete({ onPlaceSelect, value, onChange }) {
  const map = useMap();
  const places = useMapsLibrary('places');
  const geocoding = useMapsLibrary('geocoding');
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [showPredictions, setShowPredictions] = useState(false);

  useEffect(() => {
    if (!places) return;
    setAutocompleteService(new places.AutocompleteService());
  }, [places]);

  useEffect(() => {
    if (!geocoding) return;
    setGeocoder(new geocoding.Geocoder());
  }, [geocoding]);

  const fetchPredictions = useCallback((input) => {
    if (!autocompleteService || !input) {
      setPredictions([]);
      return;
    }

    autocompleteService.getPlacePredictions(
      { 
        input,
        componentRestrictions: { country: 'no' },
        types: ['geocode']
      },
      (predictions) => {
        setPredictions(predictions || []);
        setShowPredictions(true);
      }
    );
  }, [autocompleteService]);

  const handleSelect = (prediction) => {
    if (!geocoder) return;
    
    geocoder.geocode({ placeId: prediction.place_id }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        onPlaceSelect({
          lat: location.lat(),
          lng: location.lng(),
          address: results[0].formatted_address
        });
        onChange(prediction.structured_formatting.main_text);
        setShowPredictions(false);
      }
    });
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
        <Input
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            fetchPredictions(e.target.value);
          }}
          onFocus={() => value && predictions.length > 0 && setShowPredictions(true)}
          placeholder="Søk adresse..."
          className="pl-10 pr-10 h-12 text-base border-2 border-slate-200 focus:border-teal-500 rounded-xl"
        />
        {value && (
          <button
            onClick={() => {
              onChange('');
              setPredictions([]);
              setShowPredictions(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {showPredictions && predictions.length > 0 && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setShowPredictions(false)}
          />
          <div className="absolute z-50 w-full mt-2 bg-white border-2 border-slate-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto">
            {predictions.map((prediction) => (
              <button
                key={prediction.place_id}
                onClick={() => handleSelect(prediction)}
                className="w-full text-left px-4 py-3 hover:bg-teal-50 transition-colors border-b border-slate-100 last:border-0"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {prediction.structured_formatting.main_text}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {prediction.structured_formatting.secondary_text}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
